import React, { useEffect, useRef, useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import API_BASE_URL from '../../config/api';

/**
 * UniformCheckInModal
 * --------------------
 * Forces a live front-camera verification photo (NOT a camera-roll
 * upload) once per technician per calendar day, before they save their
 * first in-suite inspection of the day. The captured frame is overlaid
 * with the technician's name and a date/time stamp before being
 * uploaded to Back4App Files, then logged to the DailyTechnicianCheckIn
 * class via /api/field/daily-check-in.
 *
 * The "live camera only" guarantee is provided by getUserMedia — we
 * never expose an <input type="file">, so the technician can't slip in
 * an old photo from their camera roll. Browsers without camera access
 * (rare on a field iPhone) fall back to an inline error.
 *
 * Props:
 *   open          (bool)    — controls visibility
 *   technician    (string)  — worker name, required
 *   date          (string)  — YYYY-MM-DD (Toronto local)
 *   buildingId    (string)  — current building (for analytics tagging)
 *   buildingName  (string)
 *   onComplete    (fn)      — called with {photoUrl, createdAtEpoch} after success
 *   onCancel      (fn|null) — optional. If null, the modal is non-dismissible.
 */
export default function UniformCheckInModal({
  open,
  technician,
  date,
  buildingId,
  buildingName,
  onComplete,
  onCancel,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState('init'); // init | requesting | live | captured | uploading | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const capturedBlobRef = useRef(null);
  const capturedAtEpochRef = useRef(null);

  // ---- start / stop camera ----
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setErrorMsg('');
    setPhase('requesting');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support live camera capture.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 1280 },
          height: { ideal: 1280 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait for metadata so the video element has real dimensions
        await new Promise((res) => {
          if (!videoRef.current) return res();
          if (videoRef.current.readyState >= 2) return res();
          videoRef.current.onloadedmetadata = () => res();
        });
        try { await videoRef.current.play(); } catch {}
      }
      setPhase('live');
    } catch (err) {
      console.error('[UniformCheckIn] getUserMedia failed:', err);
      let msg = 'Couldn\'t access your front camera. Please allow camera access and try again.';
      if (err && err.name === 'NotAllowedError') {
        msg = 'Camera permission was denied. Open your browser settings and allow camera access for this site, then refresh.';
      } else if (err && err.name === 'NotFoundError') {
        msg = 'No camera was found on this device.';
      }
      setErrorMsg(msg);
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    if (!open) {
      stopStream();
      setPhase('init');
      setPreviewDataUrl(null);
      capturedBlobRef.current = null;
      capturedAtEpochRef.current = null;
      return;
    }
    startCamera();
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- date/time overlay ----
  const formatStamp = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    const wd = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
    const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    let h = d.getHours();
    const m = d.getMinutes();
    const s = d.getSeconds();
    const period = h < 12 ? 'AM' : 'PM';
    h = h % 12 || 12;
    return `${wd} ${mo} ${pad(d.getDate())}, ${d.getFullYear()} • ${h}:${pad(m)}:${pad(s)} ${period}`;
  };

  const drawTimestampOverlay = (ctx, w, h, stampText, techText) => {
    const bandHeight = Math.max(64, Math.round(h * 0.12));
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, h - bandHeight, w, bandHeight);

    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';

    const techSize = Math.max(20, Math.round(bandHeight * 0.36));
    ctx.font = `700 ${techSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Arial, sans-serif`;
    ctx.fillText(techText || '', 18, h - bandHeight + bandHeight * 0.34);

    const stampSize = Math.max(16, Math.round(bandHeight * 0.28));
    ctx.font = `500 ${stampSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Arial, sans-serif`;
    ctx.fillStyle = '#e6f0ff';
    ctx.fillText(stampText, 18, h - bandHeight + bandHeight * 0.72);

    const tag = 'HUDSON · Uniform & Name Tag Check';
    const tagSize = Math.max(14, Math.round(bandHeight * 0.22));
    ctx.font = `600 ${tagSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Arial, sans-serif`;
    ctx.fillStyle = '#ffd166';
    ctx.textAlign = 'right';
    ctx.fillText(tag, w - 18, h - bandHeight + bandHeight * 0.5);
    ctx.textAlign = 'start';
  };

  // ---- capture frame ----
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const vw = video.videoWidth || 1080;
    const vh = video.videoHeight || 1080;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvasRef.current = canvas;
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext('2d');

    // Mirror the front-camera preview so the captured image matches what
    // the technician sees on screen (otherwise text on the uniform
    // appears reversed).
    ctx.save();
    ctx.translate(vw, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, vw, vh);
    ctx.restore();

    const now = new Date();
    capturedAtEpochRef.current = Math.floor(now.getTime() / 1000);
    drawTimestampOverlay(ctx, vw, vh, formatStamp(now), technician || '');

    canvas.toBlob((blob) => {
      if (!blob) {
        setErrorMsg('Failed to capture frame. Please retake.');
        return;
      }
      capturedBlobRef.current = blob;
      setPreviewDataUrl(canvas.toDataURL('image/jpeg', 0.82));
      setPhase('captured');
      stopStream();
    }, 'image/jpeg', 0.86);
  }, [technician, stopStream]);

  const retake = useCallback(() => {
    capturedBlobRef.current = null;
    capturedAtEpochRef.current = null;
    setPreviewDataUrl(null);
    startCamera();
  }, [startCamera]);

  // ---- upload ----
  const sanitize = (s) => String(s || 'tech').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 30);

  const uploadAndRegister = useCallback(async () => {
    if (!capturedBlobRef.current) return;
    setPhase('uploading');
    setErrorMsg('');
    try {
      const APP_ID = process.env.REACT_APP_B4A_APP_ID || 'JPeNcGnGVRxgfjR3k0WTs3fbP6Vu4AagMGFXanwd';
      const MASTER_KEY = process.env.REACT_APP_B4A_MASTER_KEY || 'Z2fZSh0JCZQQpBpR4VTjJya7xF0qNSnqhc0oyLSD';
      const SERVER = process.env.REACT_APP_B4A_SERVER_URL || 'https://parseapi.back4app.com';

      let file = new File([capturedBlobRef.current], `verification_${sanitize(technician)}_${date}.jpg`, { type: 'image/jpeg' });
      try {
        file = await imageCompression(file, {
          maxSizeMB: 0.35,
          maxWidthOrHeight: 1400,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: 0.78,
        });
      } catch (err) {
        console.warn('[UniformCheckIn] compression failed, uploading raw:', err);
      }

      const safeName = `verification_${sanitize(technician)}_${date}_${Date.now()}.jpg`;
      const upResp = await fetch(`${SERVER}/files/${encodeURIComponent(safeName)}`, {
        method: 'POST',
        headers: {
          'X-Parse-Application-Id': APP_ID,
          'X-Parse-Master-Key': MASTER_KEY,
          'Content-Type': 'image/jpeg',
        },
        body: file,
      });
      if (!upResp.ok) {
        const body = await upResp.text().catch(() => '');
        throw new Error(`File upload failed (${upResp.status}): ${body.slice(0, 160)}`);
      }
      const upJson = await upResp.json();
      const photoUrl = upJson.url || upJson.name;
      if (!photoUrl) throw new Error('File upload returned no URL.');

      const createdAtEpoch = capturedAtEpochRef.current || Math.floor(Date.now() / 1000);

      const token = localStorage.getItem('managerToken') || '';
      const regResp = await fetch(`${API_BASE_URL}/api/field/daily-check-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          technician,
          date,
          photoUrl,
          createdAtEpoch,
          buildingId: buildingId || '',
          buildingName: buildingName || '',
        }),
      });
      if (!regResp.ok && regResp.status !== 409) {
        const body = await regResp.text().catch(() => '');
        console.warn('[UniformCheckIn] registration failed:', regResp.status, body);
      }

      try {
        const key = `hudson_uniform_check_in_${technician}_${date}`;
        localStorage.setItem(key, JSON.stringify({ photoUrl, createdAtEpoch }));
      } catch {}

      setPhase('done');
      onComplete && onComplete({ photoUrl, createdAtEpoch });
    } catch (err) {
      console.error('[UniformCheckIn] upload failed:', err);
      setErrorMsg(err.message || 'Upload failed. Please try again.');
      setPhase('captured');
    }
  }, [buildingId, buildingName, date, onComplete, technician]);

  if (!open) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={dotStyle} />
            <h2 style={titleStyle}>Uniform & Name Tag Check</h2>
          </div>
          {onCancel && phase !== 'uploading' && phase !== 'done' && (
            <button type="button" onClick={onCancel} style={closeBtnStyle} aria-label="Close">×</button>
          )}
        </div>

        <p style={subtitleStyle}>
          Take a quick live verification photo before your first unit today so we can confirm your uniform & name tag.
          The photo is automatically time-stamped.
        </p>
        <p style={metaLineStyle}>
          <strong>{technician || '—'}</strong>
          <span style={{ color: '#9ca3af' }}> · </span>
          <span>{date}</span>
        </p>

        <div style={stageStyle}>
          {phase === 'requesting' && (
            <div style={msgStyle}>Requesting camera access…</div>
          )}
          {phase === 'error' && (
            <div style={errorBoxStyle}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Camera unavailable</div>
              <div>{errorMsg}</div>
              <button type="button" onClick={startCamera} style={primaryBtnStyle}>Try again</button>
            </div>
          )}
          {(phase === 'live' || phase === 'requesting') && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                ...videoStyle,
                display: phase === 'live' ? 'block' : 'none',
                transform: 'scaleX(-1)',
              }}
            />
          )}
          {(phase === 'captured' || phase === 'uploading' || phase === 'done') && previewDataUrl && (
            <img src={previewDataUrl} alt="Verification photo preview" style={videoStyle} />
          )}
        </div>

        {errorMsg && phase !== 'error' && (
          <div style={inlineErrStyle}>{errorMsg}</div>
        )}

        <div style={actionsStyle}>
          {phase === 'live' && (
            <button type="button" onClick={captureFrame} style={primaryBtnStyle}>
              Take verification photo
            </button>
          )}
          {phase === 'captured' && (
            <>
              <button type="button" onClick={retake} style={secondaryBtnStyle}>Retake</button>
              <button type="button" onClick={uploadAndRegister} style={primaryBtnStyle}>
                Use this photo
              </button>
            </>
          )}
          {phase === 'uploading' && (
            <button type="button" disabled style={{ ...primaryBtnStyle, opacity: 0.6, cursor: 'not-allowed' }}>
              Uploading…
            </button>
          )}
          {phase === 'done' && (
            <button type="button" disabled style={{ ...primaryBtnStyle, background: '#1c9550' }}>
              Verified ✓
            </button>
          )}
        </div>

        <p style={footnoteStyle}>
          Live camera only. Photos can't be selected from your camera roll.
        </p>
      </div>
    </div>
  );
}

// ----- styles -----
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(8, 14, 28, 0.78)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};
const modalStyle = {
  background: '#ffffff',
  borderRadius: 18,
  width: '100%',
  maxWidth: 480,
  padding: 22,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
};
const titleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: '#1F3A68',
};
const dotStyle = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#ef4444',
  boxShadow: '0 0 0 4px rgba(239, 68, 68, 0.15)',
};
const subtitleStyle = {
  margin: '6px 0 4px',
  color: '#4a5468',
  fontSize: 13.5,
  lineHeight: 1.45,
};
const metaLineStyle = {
  margin: '0 0 14px',
  fontSize: 13,
  color: '#1a2236',
};
const stageStyle = {
  background: '#0b1424',
  borderRadius: 14,
  overflow: 'hidden',
  aspectRatio: '1 / 1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
};
const videoStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};
const msgStyle = {
  color: '#cbd5e1',
  fontSize: 14,
};
const errorBoxStyle = {
  padding: 16,
  color: '#fff',
  textAlign: 'center',
  fontSize: 13,
  lineHeight: 1.5,
};
const inlineErrStyle = {
  marginTop: 10,
  padding: '8px 12px',
  borderRadius: 8,
  background: '#fde8e8',
  color: '#B62525',
  fontSize: 13,
};
const actionsStyle = {
  display: 'flex',
  gap: 10,
  marginTop: 14,
};
const primaryBtnStyle = {
  flex: 1,
  padding: '12px 16px',
  border: 'none',
  borderRadius: 12,
  background: '#1F3A68',
  color: '#fff',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};
const secondaryBtnStyle = {
  flex: 1,
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: 12,
  background: '#ffffff',
  color: '#374151',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};
const closeBtnStyle = {
  background: 'transparent',
  border: 'none',
  fontSize: 26,
  lineHeight: 1,
  color: '#94a3b8',
  cursor: 'pointer',
};
const footnoteStyle = {
  margin: '10px 0 0',
  fontSize: 11,
  color: '#7a8499',
  textAlign: 'center',
};
